const PAN_HINT_DIRECTIONS = ["up", "down", "left", "right"] as const;

/** Décalage écran (pixels) appliqué par un clic sur chaque flèche — voir `MapViewCrosshair.show`. */
const PAN_STEP_PX = 80;

const PAN_DELTA: Record<(typeof PAN_HINT_DIRECTIONS)[number], [number, number]> = {
  up: [0, -PAN_STEP_PX],
  down: [0, PAN_STEP_PX],
  left: [-PAN_STEP_PX, 0],
  right: [PAN_STEP_PX, 0],
};

/**
 * Croix au centre + flèches de déplacement aux 4 extrémités de `container`, affichées pendant
 * l'édition interactive de la vue par défaut de la carte (voir
 * `MapLibreAdapter.startEditingMapView`) :
 * - la croix (juste 2 traits, pas de cercle/halo autour) matérialise visuellement le point exact
 *   qui deviendra le nouveau centre si l'utilisateur clique "Enregistrer" ;
 * - les 4 flèches, une par bord de l'écran (haut/bas/gauche/droite, animées en CSS, voir
 *   `style.css`), suggèrent le geste "glisser pour déplacer la carte" — et sont elles-mêmes
 *   cliquables : un clic décale la vue de `PAN_STEP_PX` pixels dans cette direction (voir
 *   `onPan`/`MapLibreAdapter.panByScreenPixels`), en plus du glisser natif. Molette/clic-droit-
 *   glisser pour zoomer/pivoter restent décrits uniquement dans le texte du tutoriel (voir
 *   `createTokaMap.ts`), sans icône dédiée. Dessinées entièrement en CSS (bordures transparentes +
 *   1 bordure pleine formant un triangle, voir `.toka-map-view-pan-hint--*` dans style.css) plutôt
 *   qu'avec un glyphe Unicode (▲▼◀▶) : ces `<div>` restent donc vides (pas de `textContent`), leur
 *   forme vient uniquement de leurs bordures.
 */
export class MapViewCrosshair {
  private crosshairElement: HTMLDivElement | null = null;
  private panHintElements: HTMLDivElement[] = [];

  constructor(private container: HTMLElement) {}

  /** `onPan(dx, dy)` : appelé avec le décalage écran (pixels) à appliquer quand une flèche est
   * cliquée — voir `MapLibreAdapter.panByScreenPixels`, que `createTokaMap` y branche directement. */
  show(onPan: (dx: number, dy: number) => void): void {
    if (this.crosshairElement) return;

    this.crosshairElement = document.createElement("div");
    this.crosshairElement.className = "toka-map-view-crosshair";

    const horizontal = document.createElement("div");
    horizontal.className = "toka-map-view-crosshair__line toka-map-view-crosshair__line--h";
    this.crosshairElement.appendChild(horizontal);

    const vertical = document.createElement("div");
    vertical.className = "toka-map-view-crosshair__line toka-map-view-crosshair__line--v";
    this.crosshairElement.appendChild(vertical);

    this.container.appendChild(this.crosshairElement);

    // Positionnées par rapport aux bords de `container`, pas au point central de la croix (repère
    // naturellement différent) : des éléments indépendants, pas des enfants de la croix.
    for (const direction of PAN_HINT_DIRECTIONS) {
      const hint = document.createElement("div");
      hint.className = `toka-map-view-pan-hint toka-map-view-pan-hint--${direction}`;
      const [dx, dy] = PAN_DELTA[direction];
      hint.addEventListener("click", () => onPan(dx, dy));
      this.container.appendChild(hint);
      this.panHintElements.push(hint);
    }
  }

  hide(): void {
    this.crosshairElement?.remove();
    this.crosshairElement = null;
    for (const hint of this.panHintElements) hint.remove();
    this.panHintElements = [];
  }
}
